import { useCallback, useEffect, useRef, useState } from 'react';

// Owns the getUserMedia lifecycle and the <video> element. Shared by the tan
// filter and the skin-analysis wizard. Attach the returned videoRef to a hidden
// <video> and read `ready`/`dimensions` to drive a canvas render loop.
export default function useCameraStream({ facingMode = 'user' } = {}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;

        await new Promise((resolve) => {
          video.onloadeddata = () => {
            const checkSize = () => {
              if (video.videoWidth > 0 && video.videoHeight > 0) {
                video.play().then(resolve).catch(() => resolve());
              } else {
                requestAnimationFrame(checkSize);
              }
            };
            checkSize();
          };
        });
        if (cancelled) return;
        setDimensions({ width: video.videoWidth, height: video.videoHeight });
        setReady(true);
      } catch (err) {
        console.error('useCameraStream: getUserMedia failed', err);
        setError(err);
      }
    };

    start();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setReady(false);
    };
  }, [facingMode]);

  // Snapshot the current video frame to a detached canvas (used for the wizard's
  // silent captures). Returns null if the stream isn't ready.
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    return canvas;
  }, []);

  return { videoRef, ready, dimensions, error, captureFrame };
}
